import { useEffect, useRef } from 'react';

/** Claim/task identity shared from the main window to the popout window. */
export interface DocumentsPopoutClaim {
  ses: string;
  idSin: string;
  taskId?: string;
  tenantSlug?: string;
  /**
   * Claim metadata for the popout's page header ("Documents · Dossier
   * {dossierNumber}" + `client` subtitle, see {@link buildPopoutHeaderTitle}).
   * Only the popout consumes these; the main-window card ignores them. All
   * optional — the header degrades gracefully (omits the parts it lacks) so a
   * claim without, say, a dossier number still renders a valid title.
   *
   * - `dossierNumber` is VEOS `NCBT_NCIE`, which is usually ALREADY a composite
   *   "{NCBT_SIN} | {company ref}" (e.g. "24337096 | B24H00106400");
   * - `contractRef` is VEOS `NCBT_SIN` — normally one of those parts, so the
   *   title only appends it when `dossierNumber` does not already hold it.
   */
  dossierNumber?: string;
  contractRef?: string;
  /**
   * The claim's client — the insured (`PASS_NOM_PER`, `ClaimSummary.client`,
   * the name `ClaimSummaryCard` shows in its header bar) — NOT the apporteur /
   * insurance company (`PAPP_NOM_PER`).
   */
  client?: string;
}

const CHANNEL_NAME = 'claimflow.documents-popout';
const POPOUT_WINDOW_NAME = 'claimflow-documents-popout';
const POPOUT_FEATURES =
  'popup=yes,width=1024,height=768,menubar=no,toolbar=no,location=no,status=no,resizable=yes,scrollbars=yes';

/** BroadcastChannel is unavailable in some test/SSR environments — guard it. */
function hasBroadcastChannel(): boolean {
  return typeof BroadcastChannel !== 'undefined';
}

// Lazily-created singleton sender channel (reused across broadcasts).
let senderChannel: BroadcastChannel | null | undefined;
function getSenderChannel(): BroadcastChannel | null {
  if (senderChannel === undefined) {
    senderChannel = hasBroadcastChannel() ? new BroadcastChannel(CHANNEL_NAME) : null;
  }
  return senderChannel;
}

// ---------------------------------------------------------------------------
// Channel messages
//
// Two messages travel on the one `claimflow.documents-popout` channel:
//
// - the CLAIM SYNC (main window → popout): the bare `DocumentsPopoutClaim`
//   object, with NO `type` field — its historic wire shape, kept as-is so a
//   popout still running an older build keeps following the main window;
// - the "RÉPONDRE" REQUEST (popout → main window, mahhp 22227): a
//   `DocumentsPopoutEmailReplyMessage`, tagged with `type`. The claim listener
//   ignores every tagged message and the reply listener every untagged one, so
//   neither window mistakes one for the other (a BroadcastChannel also delivers
//   to the OTHER channel objects of the sending window).
// ---------------------------------------------------------------------------

/** Discriminator of the "Répondre" message. */
export const EMAIL_REPLY_MESSAGE_TYPE = 'claimflow.documents-popout.email-reply';

/** A "Répondre" click in the popout, for the main window's Email card. */
export interface DocumentsPopoutEmailReply {
  /** The popout's claim — the main window's Email card checks it against its own. */
  idSin: string;
  /** The received e-mail's GED id (`ID_ADOC`, as a string). */
  idAdoc: string;
  /** The e-mail's label (`LIB_ADOC`). */
  label: string;
}

export interface DocumentsPopoutEmailReplyMessage extends DocumentsPopoutEmailReply {
  type: typeof EMAIL_REPLY_MESSAGE_TYPE;
}

function isRecord(data: unknown): data is Record<string, unknown> {
  return typeof data === 'object' && data !== null;
}

/** The untagged claim-sync message (see "Channel messages" above). */
function isClaimMessage(data: unknown): data is DocumentsPopoutClaim {
  return (
    isRecord(data) &&
    !('type' in data) &&
    typeof data.ses === 'string' &&
    typeof data.idSin === 'string'
  );
}

function isEmailReplyMessage(data: unknown): data is DocumentsPopoutEmailReplyMessage {
  return (
    isRecord(data) &&
    data.type === EMAIL_REPLY_MESSAGE_TYPE &&
    typeof data.idSin === 'string' &&
    typeof data.idAdoc === 'string' &&
    typeof data.label === 'string'
  );
}

/** Post the current claim to any open popout window (no-op if none listening). */
export function broadcastPopoutClaim(claim: DocumentsPopoutClaim): void {
  getSenderChannel()?.postMessage(claim);
}

/**
 * Post a "Répondre" request from the popout window to the main window, whose
 * claim view relays it to its Email card (no-op if no main window listens).
 */
export function broadcastEmailReplyRequest(request: DocumentsPopoutEmailReply): void {
  const message: DocumentsPopoutEmailReplyMessage = {
    type: EMAIL_REPLY_MESSAGE_TYPE,
    idSin: request.idSin,
    idAdoc: request.idAdoc,
    label: request.label,
  };
  getSenderChannel()?.postMessage(message);
}

/**
 * Bring the window that opened the popout (the main claim view) to the front.
 * Best-effort: browsers may ignore a programmatic focus, and the opener may be
 * gone (closed, or the popout opened on its own) — then nothing happens.
 */
export function focusOpenerWindow(): void {
  try {
    const opener = window.opener as Window | null;
    if (opener && !opener.closed) opener.focus();
  } catch {
    // Cross-origin or detached opener — focusing is only a nicety.
  }
}

/** Trimmed, non-empty parts of a "|"-separated VEOS reference. */
function referenceParts(reference: string | undefined): string[] {
  return (reference ?? '')
    .split('|')
    .map((part) => part.trim())
    .filter((part) => part !== '');
}

/**
 * Compose the popout header title from the claim's references, matching the
 * mockup ("Documents · Dossier 25700751 | B25H00106010", i.e.
 * `Dossier {NCBT_NCIE}`).
 *
 * `dossierNumber` (`NCBT_NCIE`) usually already holds the claim number
 * (`NCBT_SIN`, our `contractRef`) as one of its "|"-separated parts, so
 * `contractRef` is appended only when it is not already one of those parts
 * (compared trimmed, case-insensitively) — never "… | A25039864 | B25H… |
 * A25039864". Without a dossier number the title falls back to `contractRef`
 * alone, then to a bare "Documents".
 */
export function buildPopoutHeaderTitle(claim: DocumentsPopoutClaim): string {
  const dossierParts = referenceParts(claim.dossierNumber);
  const contractRef = claim.contractRef?.trim() ?? '';
  const refs: string[] = [];
  if (dossierParts.length > 0) refs.push(`Dossier ${dossierParts.join(' | ')}`);
  const alreadyInDossier = dossierParts.some(
    (part) => part.toLowerCase() === contractRef.toLowerCase(),
  );
  if (contractRef !== '' && !alreadyInDossier) refs.push(contractRef);
  return refs.length > 0 ? `Documents · ${refs.join(' | ')}` : 'Documents';
}

/**
 * Build the popout route URL for a claim's Documents panel: under the task
 * (`/task/:taskId/documents-popout`) when there is one, else on the task-less
 * popout route (`/documents-popout` — a claim opened from the claim search,
 * mahhp 22861).
 */
export function buildDocumentsPopoutUrl(claim: {
  tenantSlug: string;
  taskId?: string;
  ses: string;
  idSin: string;
  dossierNumber?: string;
  contractRef?: string;
  client?: string;
}): string {
  const params = new URLSearchParams({ ses: claim.ses, id_sin: claim.idSin });
  // Header metadata is carried in the URL too (not only over the channel) so
  // the popout shows its title on first open, before any broadcast arrives.
  if (claim.dossierNumber) params.set('dossier', claim.dossierNumber);
  if (claim.contractRef) params.set('contract', claim.contractRef);
  if (claim.client) params.set('client', claim.client);
  const base = `/claimflow/${encodeURIComponent(claim.tenantSlug)}`;
  const path = claim.taskId
    ? `${base}/task/${encodeURIComponent(claim.taskId)}/documents-popout`
    : `${base}/documents-popout`;
  return `${path}?${params.toString()}`;
}

// Module-level handle so the same popout window is reused/refocused across
// DocumentsCard remounts (the card unmounts on dashboard round-trips). Using a
// stable window NAME also lets window.open reuse an orphaned popup after a main
// reload dropped this ref.
let popoutWindow: Window | null = null;

/**
 * Open the Documents popout (or focus it if already open). When a live handle
 * exists we only focus — the BroadcastChannel keeps its content in sync — so no
 * reload happens. Otherwise open (reusing any window that already carries our
 * stable name), which navigates it to `url` (the current task) and focuses it.
 */
export function openDocumentsPopout(url: string): void {
  if (popoutWindow && !popoutWindow.closed) {
    popoutWindow.focus();
    return;
  }
  popoutWindow = window.open(url, POPOUT_WINDOW_NAME, POPOUT_FEATURES) ?? null;
  popoutWindow?.focus();
}

/** Subscribe to the channel, handing every message's payload to `onMessage`. */
function useDocumentsChannel(onMessage: (data: unknown) => void): void {
  // Latest-ref pattern so the (once-mounted) channel handler always calls the
  // freshest callback without re-subscribing. Kept off the render path (updated
  // in an effect, never mutated during render).
  const cbRef = useRef(onMessage);
  useEffect(() => {
    cbRef.current = onMessage;
  }, [onMessage]);

  useEffect(() => {
    if (!hasBroadcastChannel()) return;
    const channel = new BroadcastChannel(CHANNEL_NAME);
    channel.onmessage = (event: MessageEvent) => {
      cbRef.current(event.data);
    };
    return () => {
      channel.close();
    };
  }, []);
}

/** Subscribe to claim updates broadcast by the main window. */
export function usePopoutClaimListener(
  onClaim: (claim: DocumentsPopoutClaim) => void,
): void {
  useDocumentsChannel((data) => {
    if (isClaimMessage(data)) onClaim(data);
  });
}

/**
 * Subscribe (main window) to the "Répondre" requests posted by the Documents
 * popout. The handler gets the request without its `type` tag.
 */
export function usePopoutEmailReplyListener(
  onReply: (request: DocumentsPopoutEmailReply) => void,
): void {
  useDocumentsChannel((data) => {
    if (!isEmailReplyMessage(data)) return;
    onReply({ idSin: data.idSin, idAdoc: data.idAdoc, label: data.label });
  });
}
